import type { CameraPtzAction } from '../../models/entities/camera-ptz.model';

/** Virtual frame used when the pad does not supply video size. */
export const DEFAULT_PTZ_FRAME_SIZE = { width: 1000, height: 1000 } as const;

export interface DealerPtzZoomCommand {
  path: 'zoom';
  body: { zoomIn: boolean };
}

export interface DealerPtzCenterCommand {
  path: 'center';
  body: {
    frameWidth: number;
    frameHeight: number;
    offsetX: number;
    offsetY: number;
  };
}

export type DealerPtzCommand = DealerPtzZoomCommand | DealerPtzCenterCommand;

export interface PtzFrameSize {
  width: number;
  height: number;
}

/** Scalar `cameraId` is a positive int32. Rejects NaN, 0, negatives, and prefixed ids like `cam-5`. */
export function requirePositiveCameraId(cameraId: number | string): number {
  const raw = typeof cameraId === 'string' ? cameraId.trim() : cameraId;
  const id = typeof raw === 'string' && raw !== '' ? Number(raw) : Number(raw);
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error('Invalid camera id');
  }
  return id;
}

function resolveFrame(frame?: PtzFrameSize): PtzFrameSize {
  const width = frame?.width;
  const height = frame?.height;
  if (
    Number.isInteger(width) &&
    Number.isInteger(height) &&
    (width ?? 0) > 0 &&
    (height ?? 0) > 0
  ) {
    return { width: width as number, height: height as number };
  }
  return { width: DEFAULT_PTZ_FRAME_SIZE.width, height: DEFAULT_PTZ_FRAME_SIZE.height };
}

/**
 * Maps the live PTZ pad onto Scalar `PtzZoomRequest` / `PtzCenterRequest`.
 * Pan/tilt use a quarter-frame click on the given (or default) frame.
 * Home / presets / stop have no Dealer route.
 */
export function mapCameraPtzActionToDealer(
  action: CameraPtzAction,
  frame?: PtzFrameSize,
): DealerPtzCommand | null {
  if (action === 'zoom-in') {
    return { path: 'zoom', body: { zoomIn: true } };
  }
  if (action === 'zoom-out') {
    return { path: 'zoom', body: { zoomIn: false } };
  }
  if (action === 'home' || action === 'presets' || action === 'stop') {
    return null;
  }

  const { width, height } = resolveFrame(frame);
  const centerX = Math.floor(width / 2);
  const centerY = Math.floor(height / 2);
  const quarterX = Math.floor(width / 4);
  const quarterY = Math.floor(height / 4);

  if (action === 'left') {
    return {
      path: 'center',
      body: { frameWidth: width, frameHeight: height, offsetX: quarterX, offsetY: centerY },
    };
  }
  if (action === 'right') {
    return {
      path: 'center',
      body: {
        frameWidth: width,
        frameHeight: height,
        offsetX: width - quarterX,
        offsetY: centerY,
      },
    };
  }
  if (action === 'up') {
    return {
      path: 'center',
      body: { frameWidth: width, frameHeight: height, offsetX: centerX, offsetY: quarterY },
    };
  }
  return {
    path: 'center',
    body: {
      frameWidth: width,
      frameHeight: height,
      offsetX: centerX,
      offsetY: height - quarterY,
    },
  };
}
